import { useEditor, useVideoCreatorStore } from '@src/stores-v2';
import { ExtraElementData } from '@src/types.ts/story';
import { ElementState } from '@src/renderer/ElementState';
import { VIDEO_DEFAULT_WIDTH } from '@src/stores/VideoCreatorStore';

export const LAYOUT_TYPES = ['stacked-1-1', 'stacked-1-3'];
export const NON_LAYOUT_TYPES = ['cover', 'contain', 'fill'];

type ReturnType = {
  hasMixedOptions: boolean;
  updateFitProperty: (
    newValue: string | null,
    elementId: string,
  ) => Promise<void>;
  determineValueForStackedLayout: (
    elementId: string,
    initialValue?: string,
  ) => string | undefined;
  handleStackedLayoutFitChange: (value: string | null) => Promise<void>;
};

export default function useLayoutAwareFitPropertyState({
  options,
  propertyName,
  defaultValue,
  activeElement,
  updateFitPropertyOverride,
}: {
  options: Array<{ caption: string; value: any }>;
  propertyName: string;
  defaultValue: any;
  activeElement: ElementState;
  updateFitPropertyOverride?: (newValue: string | null, elementId: string) => Promise<void>;
}): ReturnType {
  const videoCreator = useVideoCreatorStore();
  const videoSourceStore = videoCreator.rootStore.videoSourceStore;
  const editorStore = useEditor();

  // Check if we're dealing with a dropdown that has both regular fit and layout options
  const hasMixedOptions =
    options.some((option) => LAYOUT_TYPES.includes(option.value)) &&
    options.some((option) => NON_LAYOUT_TYPES.includes(option.value));

  const updateFitProperty = async (
    newValue: string | null,
    elementId: string,
  ) => {
    if (updateFitPropertyOverride) {
      // Use the provided override function (e.g., headless editor)
      await updateFitPropertyOverride(newValue, elementId);
    } else {
      // Fall back to legacy method
      const modifications = {
        [`${elementId}.${propertyName}`]: newValue,
      };

      await videoCreator.applyVideoStateModifications(
        modifications,
        true,
        'applying inner video fit',
      );
    }
  };

  const determineValueForStackedLayout = (
    elementId: string,
    initialValue: any,
  ): string | undefined => {
    let value = initialValue;
    // For direct stacked layout info from the element itself
    let eed = videoCreator.currentVideo!.extraElementData[
      elementId
    ] as ExtraElementData | null;

    // Check if this is a hidden parent element with a layout
    const isHiddenParentElement =
      eed && eed.layoutType && LAYOUT_TYPES.includes(eed.layoutType);

    if (isHiddenParentElement && eed) {
      // Case 1: This is a hidden parent element with layout info
      value = eed.layoutType;
    }
    // Case 2: This is a composition element in a layout
    else if (
      eed &&
      eed.layoutPosition &&
      eed.layoutPosition !== 'hidden' &&
      eed.layoutAssociatedIds
    ) {
      const hiddenElementId = eed.layoutAssociatedIds[0];
      const hiddenEed = videoCreator.currentVideo!.extraElementData[
        hiddenElementId
      ] as ExtraElementData | null;

      if (
        hiddenEed &&
        hiddenEed.layoutType &&
        LAYOUT_TYPES.includes(hiddenEed.layoutType)
      ) {
        value = hiddenEed.layoutType;
      }
    }
    // Case 3: None of the above special cases, check if the current value is valid
    else {
      // If not a layout or inner video element, and the value isn't valid, use default
      if (!LAYOUT_TYPES.includes(value) && !NON_LAYOUT_TYPES.includes(value)) {
        value = defaultValue;
      }
    }
    return value;
  };

  const handleStackedLayoutFitChange = async (value: string | null) => {
    let elementId = activeElement.source.id;

    // Check if we're dealing with a child video element inside a composition
    const isInnerVideo = isInnerVideoElement(
      elementId,
      activeElement.source.type,
      videoSourceStore.source,
    );

    // Special case: if this is an inner video element and we're only changing its fit property
    // Don't do any layout manipulations, just change this element's fit
    if (isInnerVideo) {
      await updateFitProperty(value, elementId);
      return;
    }

    // From here on, we're dealing with either:
    // 1. A composition element (could be part of a layout)
    // 2. A layout parent element (hidden)
    // 3. A standalone video element (not in a composition)

    // If this element is a composition in a layout, get its hidden parent element
    let eed = videoCreator.currentVideo!.extraElementData[
      elementId
    ] as ExtraElementData | null;
    if (
      eed &&
      eed.layoutPosition &&
      eed.layoutPosition !== 'hidden' &&
      eed.layoutAssociatedIds
    ) {
      elementId = eed.layoutAssociatedIds[0];
      eed = videoCreator.currentVideo!.extraElementData[
        elementId
      ] as ExtraElementData | null;
    }

    // Check if this is a hidden parent element with a layout
    const isHiddenParentElement =
      eed && eed.layoutType && LAYOUT_TYPES.includes(eed.layoutType);

    // Check if we're switching between layout types or to/from layouts
    const isFromLayout = isHiddenParentElement;
    const isToLayout = LAYOUT_TYPES.includes(value as string);
    const isSwitchingLayoutType =
      isFromLayout && isToLayout && value !== eed?.layoutType;
    const isExitingLayout = isFromLayout && !isToLayout;
    const isEnteringLayout = !isFromLayout && isToLayout;

    if (editorStore.headlessEnabled && elementId) {
      // Case 1: Switching from one layout type to another layout type
      if (isSwitchingLayoutType) {
        await videoCreator.reframingModeManager.exitReframingMode();

        const resultElements = editorStore.editor.commandSwitchLayoutType(
          elementId,
          value as 'stacked-1-1' | 'stacked-1-3',
          VIDEO_DEFAULT_WIDTH,
          videoCreator.currentVideo!.videoSource.width,
        );
        if (!resultElements) {
          console.warn(
            `Failed to switch layout element type for element ${elementId}`,
          );
          return;
        }

        if (resultElements) {
          await videoCreator.setActiveElements(
            resultElements.top.id || resultElements.bottom.id,
          );
        }

        videoCreator.refreshKaraokeElements();

        return;
      }
      // Case 2: Exiting a layout (switching from layout to non-layout)
      else if (isExitingLayout) {
        await videoCreator.reframingModeManager.exitReframingMode();

        const success = editorStore.editor.commandClearLayoutElements(
          elementId,
          value as 'cover' | 'contain' | 'fill',
          VIDEO_DEFAULT_WIDTH,
          videoCreator.currentVideo!.videoSource.width,
        );
        if (!success) {
          console.warn(
            `Failed to remove layout elements for element ${elementId}`,
          );
          return;
        }

        await videoCreator.setActiveElements(elementId);
        videoCreator.refreshKaraokeElements();

        return;
      }
      // Case 3: Entering layout mode (switching from non-layout to layout)
      else if (isEnteringLayout) {
        await videoCreator.reframingModeManager.exitReframingMode();

        const resultElements = editorStore.editor.commandAddLayoutElements(
          elementId,
          value as 'stacked-1-1' | 'stacked-1-3',
        );
        if (!resultElements) {
          console.warn(
            `Failed to add layout elements for element ${elementId}`,
          );
          return;
        }

        if (resultElements[0]) {
          await videoCreator.setActiveElements(resultElements[0].id);
        }
        videoCreator.refreshKaraokeElements();

        return;
      }
      // Case 4: Just changing the fit property of a non-layout element
      // fallback to legacy method
    }

    // Case 1: Switching from one layout type to another layout type
    if (isSwitchingLayoutType) {
      // We need to:
      // 1. Delete all associated elements from the current layout
      // 2. Clear the layout metadata
      // 3. Create new layout elements for the new layout type
      // 4. Keep the element hidden and locked

      // First, verify we have associated elements to delete
      if (eed?.layoutAssociatedIds && eed.layoutAssociatedIds.length > 0) {
        // Make a copy of the array to avoid modification during iteration
        const associatedIds = [...eed.layoutAssociatedIds];

        // Delete each associated element
        for (const linkedElementId of associatedIds) {
          // Clean up extraElementData for the composition element
          if (videoCreator.currentVideo!.extraElementData[linkedElementId]) {
            delete videoCreator.currentVideo!.extraElementData[linkedElementId];
          }

          // Delete the actual element
          await videoCreator.stackedLayoutManager.deleteElement(
            linkedElementId,
            false,
          );
        }
      }

      // Now clean up the layout metadata
      if (eed) {
        delete eed.layoutAssociatedIds;
        delete eed.layoutPosition;
        delete eed.layoutType;
      }

      // Create new layout elements for the new layout type

      await videoCreator.stackedLayoutManager.addLayoutElements(
        elementId,
        value as string,
        'switching layout type',
      );

      videoCreator.refreshKaraokeElements();

      return;
    }

    // Case 2: Exiting a layout (switching from layout to non-layout)
    else if (isExitingLayout) {
      // We need to:
      // 1. Delete all associated elements
      // 2. Clear the layout metadata
      // 3. Make the primary element visible and unlocked
      // 4. Set its fit property

      // First, delete associated elements
      if (eed?.layoutAssociatedIds && eed.layoutAssociatedIds.length > 0) {
        // Make a copy to avoid modification during iteration
        const associatedIds = [...eed.layoutAssociatedIds];

        // Delete each associated element
        for (const linkedElementId of associatedIds) {
          // Clean up extraElementData
          if (videoCreator.currentVideo!.extraElementData[linkedElementId]) {
            delete videoCreator.currentVideo!.extraElementData[linkedElementId];
          }

          // Delete the actual element
          await videoCreator.stackedLayoutManager.deleteElement(
            linkedElementId,
            false,
          );
        }
      }

      // Clean up the layout metadata
      if (eed) {
        delete eed.layoutAssociatedIds;
        delete eed.layoutPosition;
        delete eed.layoutType;
      }

      // Make element visible and set its fit property
      const modifications = {
        [`${elementId}.visible`]: 'true',
        [`${elementId}.locked`]: 'false',
        [`${elementId}.${propertyName}`]: value,
        // Also reset position and size
        [`${elementId}.width`]: '100%',
        [`${elementId}.height`]: '100%',
        [`${elementId}.x`]: '50%',
        [`${elementId}.y`]: '50%',
      };

      await videoCreator.applyVideoStateModifications(
        modifications,
        true,
        'exiting layout mode',
      );

      await videoCreator.setActiveElements(elementId);

      videoCreator.refreshKaraokeElements();

      return;
    }

    // Case 3: Entering layout mode (switching from non-layout to layout)
    else if (isEnteringLayout) {
      // Create new layout elements

      await videoCreator.stackedLayoutManager.addLayoutElements(
        elementId,
        value as string,
        'entering layout mode',
      );

      videoCreator.refreshKaraokeElements();

      return;
    }

    // Case 4: Just changing the fit property of a non-layout element
    else {
      // Check if this is a simple case that can use headless editor
      const hasAspectRatioAdjustments = !videoCreator.hasDefaultAspectRatio() && value === 'cover';
      
      if (!hasAspectRatioAdjustments && updateFitPropertyOverride) {
        // Simple case - use the enhanced updateFitProperty function (headless editor)
        await updateFitProperty(value, elementId);
        return;
      }
      
      // Complex case with aspect ratio adjustments - use legacy method
      const modifications = {
        [`${elementId}.${propertyName}`]: value,
      };

      // Additional adjustments for aspect ratio if needed
      if (!videoCreator.hasDefaultAspectRatio()) {
        await videoCreator.reframingModeManager.exitReframingMode();
        if (value === 'cover') {
          await videoCreator.adjustModificationsToAspectRatio(modifications, {
            ...videoCreator.getElementSource(activeElement),
            [propertyName]: value,
          });
        }
      }

      await videoCreator.applyVideoStateModifications(
        modifications,
        true,
        'updating fit property',
      );

      return;
    }
  };

  return {
    hasMixedOptions,
    updateFitProperty,
    determineValueForStackedLayout,
    handleStackedLayoutFitChange,
  };
}

export function isInnerVideoElement(
  elementId: string,
  elementType: string,
  source: Record<string, any>,
): boolean {
  if (elementType !== 'video' || !source.elements) {
    return false;
  }

  return source.elements.some((el: any) =>
    isCompositionWithElement(el, elementId),
  );
}

function isCompositionWithElement(el: any, elementId: string) {
  return (
    el.type === 'composition' &&
    el.elements &&
    Array.isArray(el.elements) &&
    el.elements.some((child: any) => child.id === elementId)
  );
}
